import context from "./purchaseOrderContext";
import React, {useContext, useEffect, useReducer, useRef} from "react";
import reducer from "./purchaseOrderReducer";
import types from "./purchaseOrderTypes";
import {NotificationContext} from "../NotificationContext";
import {AuthContext} from "../AuthContext";
import sortContext from "../sort/sortContext";


const initialState = {
	loading: false,
	purchaseOrders: [],
	pageInfo: {
		limit: 999999,
		offset: 0,
		maxOffset: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	},
	filters: {}
};


export default ({children}) => {
	const [state, dispatch] = useReducer(reducer, initialState);
	const {token, setToken} = useContext(AuthContext);
	const {addToast} = useContext(NotificationContext);
	const {asc, desc, sortBy} = useContext(sortContext);
	const abortController = useRef(null);

	useEffect(() => {
		dispatch({
			type: types.SET_PAGE_INFO,
			payload: {
				...state.pageInfo,
				offset: 0
			}
		});
	}, [state.pageInfo.limit]);

	useEffect(() => {
		abortController.current = new AbortController();

		(async () => {
			try {
				await getPurchaseOrders();
			}
			catch (e) {
				console.error(e);
				dispatch({
					type: types.SET_LOADING
				});
			}
		})();

		return () => {
			abortController.current.abort();
		}
	}, [state.pageInfo.offset, state.pageInfo.limit, state.filters.consumed, asc, desc, sortBy]);

	async function getPurchaseOrders() {
		dispatch({
			type: types.SET_LOADING
		});

		const response = await fetch(process.env.REACT_APP_endPoint + '/graphql', {
			method: "POST",
                                                                  credentials: "include",
			signal: abortController.current.signal,
			headers: {
				"Content-Type": "application/json",
				'Authorization': 'Bearer ' + token
			},
			body: JSON.stringify({
				query: `
					query getPurchaseOrders ($pagination:PaginationInput, $filters:PurchaseOrderFilters, $sort:SortInput){
					  getPurchaseOrders (pagination:$pagination, filters:$filters, sort:$sort) {
						purchaseOrders {
						id
						number
						description
						totalValue
                        currencyId
						currency { name }
						pdfFileName
						valueConsumed
                        valueInProgress
						valueRemaining
						valueRemainingInclInProgress
						customer {
							id
							fullName
						}
                        billingEntity {
                            id
                            name
                        }
						issueDate
						comments
                        billingEntityId
                        clientContact
						archived
						changeLogs {
							id
							oldValue
							newValue
							reason
							manager {
								id
								firstName
								lastName
							}
							timestamp
						}
					  }
						pageInfo {
						  hasNextPage
						  hasPreviousPage
						  offset
						  limit
						  maxOffset
						}
					  }
					}
						`,
				variables: {
					pagination: {
						limit: state.pageInfo.limit,
						offset: state.pageInfo.offset
					},
					filters: {
						...state.filters
					},
					sort: {
						column: sortBy,
						asc,
						desc
					}
				}
			})
		});
		if (response.status === 401) {
			setToken(null);
		}
		else {
			const json = await response.json();

			if (json.errors) {
				for (let i = 0; i < json.errors.length; i++) {
					const {data} = json.errors[i];

					if (data && data.length > 0) {
						for (let j = 0; j < data.length; j++) {
							addToast({
								message: json.errors[i].message,
								description: data[j]
							});
						}
					} else {
						addToast({
							message: json.errors[i].message,
							description: ""
						});
					}
				}
				dispatch({
					type: types.SET_LOADING
				});
			} else {
				dispatch({
					type: types.SET_PURCHASE_ORDERS,
					payload: json.data.getPurchaseOrders.purchaseOrders
				});
				dispatch({
					type: types.SET_PAGE_INFO,
					payload: json.data.getPurchaseOrders.pageInfo
				})
			}
		}
	}

	async function createPurchaseOrder(purchaseOrder) {
		dispatch({
			type: types.SET_LOADING
		});

		const response = await fetch(process.env.REACT_APP_endPoint + '/graphql', {
			method: "POST",
                                                                  credentials: "include",
			headers: {
				"Content-Type": "application/json",
				'Authorization': 'Bearer ' + token
			},
			body: JSON.stringify({
				query: `
					mutation createPurchaseOrder ($purchaseOrder:CreatePurchaseOrderInput!) {
					  createPurchaseOrder(purchaseOrder: $purchaseOrder) {
						id
						number
						description
						totalValue
						currencyId
						pdfContent
						pdfFileName
						customerId
						comments
						issueDate
                        billingEntityId
                        clientContact
					  }
					}
				`,
				variables: {
					purchaseOrder: {
						...purchaseOrder,
					}
				}
			})
		});
		if (response.status === 401) {
			setToken(null);
		}
		else {
			const json = await response.json();

			if (json.errors) {
				for (let i = 0; i < json.errors.length; i++) {
					const {data} = json.errors[i];

					if (data && data.length > 0) {
						for (let j = 0; j < data.length; j++) {
							addToast({
								message: json.errors[i].message,
								description: data[j]
							});
						}
					} else {
						addToast({
							message: json.errors[i].message,
							description: ""
						});
					}
				}

				dispatch({
					type: types.SET_LOADING
				});
			} else {
				abortController.current.abort();
				abortController.current = new AbortController();
				await getPurchaseOrders();
			}
		}
	}

	async function removePurchaseOrder(purchaseOrderId) {
		dispatch({
			type: types.SET_LOADING
		});

		const response = await fetch(process.env.REACT_APP_endPoint + '/graphql', {
			method: "POST",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				'Authorization': 'Bearer ' + token
			},
			body: JSON.stringify({
				query: `
					mutation removePurchaseOrder ($purchaseOrderId: ID!){
						removePurchaseOrder(purchaseOrderId:$purchaseOrderId) 
					}
				`,
				variables: {
					purchaseOrderId: purchaseOrderId
				}
			})
		});
		if (response.status === 401) {
			setToken(null);
		}
		else {
			const json = await response.json();

			if (json.errors) {
				for (let i = 0; i < json.errors.length; i++) {
					const {data} = json.errors[i];

					if (data && data.length > 0) {
						for (let j = 0; j < data.length; j++) {
							addToast({
								message: json.errors[i].message,
								description: data[j]
							});
						}
					} else {
						addToast({
							message: json.errors[i].message,
							description: ""
						});
					}
				}
				dispatch({
					type: types.SET_LOADING
				});
			} else {
				if (state.purchaseOrders.length === 1 && state.pageInfo) {
					if (state.pageInfo.hasPreviousPage) {
						dispatch({
							type: types.SET_PAGE_INFO,
							payload: {
								...state.pageInfo,
								offset: state.pageInfo.offset - state.pageInfo.limit
							}
						});
					}
				} else {
					await getPurchaseOrders();
				}
			}
		}
	}

	async function archivePurchaseOrder(purchaseOrderId) {
		dispatch({
			type: types.SET_LOADING
		});
		
		const response = await fetch(process.env.REACT_APP_endPoint + '/graphql', {
			method: "POST",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				'Authorization': 'Bearer ' + token
			},
			body: JSON.stringify({
				query: `
					mutation archivePurchaseOrder ($purchaseOrderId: ID!){
						archivePurchaseOrder(purchaseOrderId:$purchaseOrderId)
					}
				`,
				variables: {
					purchaseOrderId: purchaseOrderId
				}
			})
		});
		if (response.status === 401) {
			setToken(null);
		}
		else {
			const json = await response.json();

			if (json.errors) {
				for (let i = 0; i < json.errors.length; i++) {
					const {data} = json.errors[i];

					if (data && data.length > 0) {
						for (let j = 0; j < data.length; j++) {
							addToast({
								message: json.errors[i].message,
								description: data[j]
							});
						}
					} else {
						addToast({
							message: json.errors[i].message,
							description: ""
						});
					}
				}
				dispatch({
					type: types.SET_LOADING
				});
			} else {
				await getPurchaseOrders();
			}
		}
	}

	async function unarchivePurchaseOrder(purchaseOrderId) {
		dispatch({
			type: types.SET_LOADING
		});
		
		const response = await fetch(process.env.REACT_APP_endPoint + '/graphql', {
			method: "POST",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				'Authorization': 'Bearer ' + token
			},
			body: JSON.stringify({
				query: `
					mutation unArchivePurchaseOrder ($purchaseOrderId: ID!){
						unArchivePurchaseOrder(purchaseOrderId:$purchaseOrderId)
					}
				`,
				variables: {
					purchaseOrderId: purchaseOrderId
				}
			})
		});
		if (response.status === 401) {
			setToken(null);
		}
		else {
			const json = await response.json();

			if (json.errors) {
				for (let i = 0; i < json.errors.length; i++) {
					const {data} = json.errors[i];

					if (data && data.length > 0) {
						for (let j = 0; j < data.length; j++) {
							addToast({
								message: json.errors[i].message,
								description: data[j]
							});
						}
					} else {
						addToast({
							message: json.errors[i].message,
							description: ""
						});
					}
				}
				dispatch({
					type: types.SET_LOADING
				});
			} else {
				await getPurchaseOrders();
			}
		}
	}

	async function editPurchaseOrder(purchaseOrderId, purchaseOrder) {
		dispatch({
			type: types.SET_LOADING
		});

		const response = await fetch(process.env.REACT_APP_endPoint + '/graphql', {
			method: "POST",
                                                                  credentials: "include",
			headers: {
				"Content-Type": "application/json",
				'Authorization': 'Bearer ' + token
			},
			body: JSON.stringify({
				query: `
					mutation editPurchaseOrder ($purchaseOrderId: ID!, $purchaseOrder:EditPurchaseOrderInput!) {
						editPurchaseOrder (purchaseOrderId:$purchaseOrderId, purchaseOrder:$purchaseOrder) {
							id
							number
							description
							totalValue
							currencyId
							pdfFileName
							pdfContent
							customerId
							issueDate
							comments
							customer {
								id
								fullName
							}
                            billingEntityId
                            clientContact
						}
					}
				`,
				variables: {
					purchaseOrderId,
					purchaseOrder: {
						number: purchaseOrder.number,
						description: purchaseOrder.description,
						totalValue: purchaseOrder.totalValue,
						currencyId: purchaseOrder.currencyId,
						pdfFileName: purchaseOrder.pdfFileName,
						pdfContent: purchaseOrder.pdfContent,
						customerId: purchaseOrder.customerId,
						issueDate: purchaseOrder.issueDate,
						comments: purchaseOrder.comments,
                        billingEntityId: purchaseOrder.billingEntityId,
                        clientContact: purchaseOrder.clientContact
					}
				}
			})
		});
		if (response.status === 401) {
			setToken(null);
		}
		else {
			const json = await response.json();

			if (json.errors) {
				for (let i = 0; i < json.errors.length; i++) {
					const {data} = json.errors[i];

					if (data && data.length > 0) {
						for (let j = 0; j < data.length; j++) {
							addToast({
								message: json.errors[i].message,
								description: data[j]
							});
						}
					} else {
						addToast({
							message: json.errors[i].message,
							description: ""
						});
					}
				}
				dispatch({
					type: types.SET_LOADING
				});
			} else {
				// refreshing list of POs because valueConsumed and valueRemaining need to be recalculated
				await getPurchaseOrders();
			}
		}
	}

	return (
		<context.Provider value={{
			...state,
			getPurchaseOrders,
			createPurchaseOrder,
			editPurchaseOrder,
			removePurchaseOrder,
			archivePurchaseOrder,
			unarchivePurchaseOrder,
			dispatch
		}}>
			{children}
		</context.Provider>
	);
}
